/**
 * Wake Lock API：播放时请求防锁屏，暂停/页面隐藏时释放
 * 含页面可见性变化时的释放与恢复，以及“锁屏导致播放中断”提示
 * 抽离自 MusicPlayer.js
 */
import { useEffect } from 'react';

/**
 * @param {object} options
 * @param {boolean} options.playing
 * @param {React.MutableRefObject<WakeLockSentinel|null>} options.wakeLockRef
 * @param {React.MutableRefObject<{ wasPlaying: boolean }>} options.lockInterruptionRef
 * @param {React.RefObject<HTMLVideoElement|null>} options.videoRef
 * @param {React.RefObject<HTMLAudioElement|null>} options.audioRef
 * @param {React.MutableRefObject<string|null>} options.activeMediaTypeRef
 * @param {function(string): void} options.showHintPlus - t('player.lockPlaybackInterrupted')
 * @param {string} options.lockPlaybackInterruptedT - 已翻译的“锁屏导致播放中断”文案
 */
export function useWakeLock({
  playing,
  wakeLockRef,
  lockInterruptionRef,
  videoRef,
  audioRef,
  activeMediaTypeRef,
  showHintPlus,
  lockPlaybackInterruptedT
}) {
  useEffect(() => {
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator && !wakeLockRef.current && !document.hidden) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
          wakeLockRef.current.addEventListener('release', () => {
            wakeLockRef.current = null;
          });
        }
      } catch (_) {
        wakeLockRef.current = null;
      }
    };

    const releaseWakeLock = async () => {
      try {
        if (wakeLockRef.current) {
          await wakeLockRef.current.release();
          wakeLockRef.current = null;
        }
      } catch (_) {
        wakeLockRef.current = null;
      }
    };

    if (playing && !document.hidden) {
      requestWakeLock();
    } else {
      releaseWakeLock();
    }

    return () => {
      releaseWakeLock();
    };
  }, [playing, wakeLockRef]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.hidden) {
        if (playing) {
          lockInterruptionRef.current = { wasPlaying: true };
        }
        if (wakeLockRef.current) {
          try {
            await wakeLockRef.current.release();
            wakeLockRef.current = null;
          } catch (_) {
            wakeLockRef.current = null;
          }
        }
      } else {
        if (playing && 'wakeLock' in navigator && !wakeLockRef.current) {
          try {
            wakeLockRef.current = await navigator.wakeLock.request('screen');
            wakeLockRef.current.addEventListener('release', () => {
              wakeLockRef.current = null;
            });
          } catch (_) {
            wakeLockRef.current = null;
          }
        }
        if (lockInterruptionRef.current?.wasPlaying) {
          const mediaEl = activeMediaTypeRef.current === 'video' ? videoRef.current : audioRef.current;
          const isStopped = !playing || (mediaEl && mediaEl.paused);
          if (isStopped) {
            showHintPlus(lockPlaybackInterruptedT);
          }
          lockInterruptionRef.current = { wasPlaying: false };
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [playing, wakeLockRef, lockInterruptionRef, activeMediaTypeRef, videoRef, audioRef, showHintPlus, lockPlaybackInterruptedT]);
}
